import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faArrowRight, faBolt, faCalendarWeek, faFire,
  faTrophy, faCheck, faChevronRight, faCircle,
} from '@fortawesome/free-solid-svg-icons';
import { AREA_CONFIG, getLevelInfo } from './LevelUpHubPage';
import api from '../api/API';
import './PlanejamentoSemanalPDIPage.css';

// ─── date helpers ────────────────────────────────────────────────────────────
function toYMD(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
function parseYMD(s) {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}
function getWeekBounds(base) {
  const d = new Date(base);
  d.setHours(0, 0, 0, 0);
  const dow = d.getDay();
  const diff = dow === 0 ? -6 : 1 - dow;
  const mon = new Date(d);
  mon.setDate(d.getDate() + diff);
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  return { start: mon, end: sun };
}
function weekDays(base) {
  const { start } = getWeekBounds(base);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}
function isToday(date) {
  const t = new Date();
  return date.getFullYear() === t.getFullYear() &&
    date.getMonth() === t.getMonth() &&
    date.getDate() === t.getDate();
}
function isPast(date) {
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return date < t;
}

const DAY_LABELS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

// ─── component ───────────────────────────────────────────────────────────────
const PlanejamentoSemanalPDIPage = () => {
  const navigate = useNavigate();
  const [baseDate, setBaseDate] = useState(new Date());

  const [metas, setMetas] = useState([]);
  const [profile, setProfile] = useState(null);
  const [semanaData, setSemanaData] = useState({}); // { ymd: { dia, tarefas } }
  const [loading, setLoading] = useState(true);
  const [weekTasks, setWeekTasks] = useState([]); // all tasks visible this week

  const days = useMemo(() => weekDays(baseDate), [baseDate]);
  const { start, end } = useMemo(() => getWeekBounds(baseDate), [baseDate]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get('/pdi/metas'),
      api.get('/pdi/profile'),
      api.get(`/dias/intervalo?inicio=${toYMD(start)}&fim=${toYMD(end)}`),
      api.get('/tasks'),
    ]).then(async ([metasRes, profileRes, intervaloRes, tasksRes]) => {
      setMetas((metasRes?.data?.records || []).filter(m => !m.concluida));
      setProfile(profileRes?.data?.data || null);

      const diasSemana = intervaloRes?.data || [];
      const allTasks = tasksRes?.data || [];
      setWeekTasks(allTasks);

      // Para cada dia da semana que já existe, pegar as tarefas
      const map = {};
      await Promise.all(diasSemana.map(async dia => {
        if (!dia?.id) return;
        try {
          const r = await api.get(`/dias/${dia.id}/tarefas`);
          map[dia.data] = {
            dia,
            tarefas: r?.data || [],
          };
        } catch { map[dia.data] = { dia, tarefas: [] }; }
      }));
      setSemanaData(map);
    }).catch(console.error).finally(() => setLoading(false));
  }, [toYMD(start)]);

  // XP potencial da semana = todas as tasks pendentes vinculadas a metas esta semana
  const weekXpPotencial = useMemo(() => {
    let xp = 0;
    Object.values(semanaData).forEach(({ tarefas }) => {
      tarefas.forEach(t => {
        if (!t.concluida && t.tipo !== 3) xp += 10;
      });
    });
    return xp;
  }, [semanaData]);

  // Metas que têm cobertura esta semana (task planejada em algum dia)
  const metasCobertasIds = useMemo(() => {
    const ids = new Set();
    Object.values(semanaData).forEach(({ tarefas }) => {
      tarefas.forEach(t => {
        if (t.id_meta_pdi) ids.add(t.id_meta_pdi);
      });
    });
    return ids;
  }, [semanaData]);

  // Distribuição por área (quantos dias têm tasks de cada área)
  const areaCoverage = useMemo(() => {
    const map = {};
    AREA_CONFIG.forEach(a => { map[a.id] = { dias: new Set(), xp: 0 }; });
    Object.values(semanaData).forEach(({ dia, tarefas }) => {
      tarefas.forEach(t => {
        const meta = metas.find(m => m.id === t.id_meta_pdi);
        if (meta?.area && map[meta.area]) {
          map[meta.area].dias.add(dia?.data);
          map[meta.area].xp += 10;
        }
      });
    });
    return AREA_CONFIG.map(a => ({
      ...a,
      diasCount: map[a.id]?.dias.size || 0,
      xp: map[a.id]?.xp || 0,
    })).filter(a => a.diasCount > 0 || a.xp > 0);
  }, [semanaData, metas]);

  // Totais da semana
  const totalPlanejadas = useMemo(() =>
    Object.values(semanaData).reduce((acc, { tarefas }) =>
      acc + tarefas.filter(t => t.tipo !== 3).length, 0), [semanaData]);

  const totalConcluidas = useMemo(() =>
    Object.values(semanaData).reduce((acc, { tarefas }) =>
      acc + tarefas.filter(t => t.tipo !== 3 && t.concluido).length, 0), [semanaData]);

  const semanaLabel = `${start.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long' })} — ${end.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long' })}`;
  const totalXp = profile?.totalXp ?? 0;
  const levelInfo = getLevelInfo(totalXp);

  if (loading) {
    return (
      <div className="psem-page">
        <div className="psem-loading">
          <div className="psem-spinner" />
          <p>Carregando semana...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="psem-page">
      <div className="psem-orb psem-orb-1" />
      <div className="psem-orb psem-orb-2" />

      {/* Header */}
      <div className="psem-header">
        <button className="psem-back-btn" onClick={() => navigate('/levelup')}>
          <FontAwesomeIcon icon={faArrowLeft} /> LevelUp Hub
        </button>

        <div className="psem-week-nav">
          <button className="psem-nav-btn" onClick={() => {
            const d = new Date(baseDate); d.setDate(d.getDate() - 7); setBaseDate(d);
          }}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>

          <div className="psem-week-info">
            <div className="psem-week-label">
              <FontAwesomeIcon icon={faCalendarWeek} style={{ color: '#6366f1', marginRight: 8 }} />
              {semanaLabel}
            </div>
            <button className="psem-this-week-btn" onClick={() => setBaseDate(new Date())}>
              Semana atual
            </button>
          </div>

          <button className="psem-nav-btn" onClick={() => {
            const d = new Date(baseDate); d.setDate(d.getDate() + 7); setBaseDate(d);
          }}>
            <FontAwesomeIcon icon={faArrowRight} />
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="psem-kpi-strip">
        <div className="psem-kpi-card">
          <div className="psem-kpi-icon" style={{ color: '#6366f1', background: 'rgba(99,102,241,0.12)' }}>
            <FontAwesomeIcon icon={faCalendarWeek} />
          </div>
          <div>
            <strong>{totalConcluidas}/{totalPlanejadas}</strong>
            <small>tarefas concluídas</small>
          </div>
        </div>
        <div className="psem-kpi-card">
          <div className="psem-kpi-icon" style={{ color: '#f59e0b', background: 'rgba(245,158,11,0.12)' }}>
            <FontAwesomeIcon icon={faBolt} />
          </div>
          <div>
            <strong>{weekXpPotencial} XP</strong>
            <small>potencial na semana</small>
          </div>
        </div>
        <div className="psem-kpi-card">
          <div className="psem-kpi-icon" style={{ color: '#10b981', background: 'rgba(16,185,129,0.12)' }}>
            <FontAwesomeIcon icon={faTrophy} />
          </div>
          <div>
            <strong>{metasCobertasIds.size}</strong>
            <small>metas com cobertura</small>
          </div>
        </div>
        <div className="psem-kpi-card">
          <div className="psem-kpi-icon" style={{ color: '#ec4899', background: 'rgba(236,72,153,0.12)' }}>
            <FontAwesomeIcon icon={faFire} />
          </div>
          <div>
            <strong>Nível {levelInfo.current.level}</strong>
            <small>{levelInfo.current.title} · {totalXp.toLocaleString('pt-BR')} XP</small>
          </div>
        </div>
      </div>

      {/* Progress bar da semana */}
      {totalPlanejadas > 0 && (
        <div className="psem-week-progress">
          <div className="psem-week-progress-bar">
            <div
              className="psem-week-progress-fill"
              style={{ width: `${Math.round((totalConcluidas / totalPlanejadas) * 100)}%` }}
            />
          </div>
          <span>{Math.round((totalConcluidas / totalPlanejadas) * 100)}% da semana concluída</span>
        </div>
      )}

      <div className="psem-body">

        {/* Day cards grid */}
        <section className="psem-section psem-days-section">
          <div className="psem-section-header">
            <h2>Dias da semana</h2>
            <span className="psem-section-hint">Clique em um dia para planejar</span>
          </div>
          <div className="psem-days-grid">
            {days.map((day, i) => {
              const ymd = toYMD(day);
              const dayData = semanaData[ymd];
              const tarefas = dayData?.tarefas?.filter(t => t.tipo !== 3) || [];
              const concluidas = tarefas.filter(t => t.concluido).length;
              const hoje = isToday(day);
              const passado = isPast(day) && !hoje;

              // Collect areas covered in this day
              const dayAreas = [...new Set(
                tarefas
                  .map(t => metas.find(m => m.id === t.id_meta_pdi)?.area)
                  .filter(Boolean)
              )];

              const pct = tarefas.length > 0
                ? Math.round((concluidas / tarefas.length) * 100)
                : 0;

              return (
                <div
                  key={ymd}
                  className={`psem-day-card ${hoje ? 'today' : ''} ${passado ? 'past' : ''}`}
                  onClick={() => navigate('/levelup/dia')}
                >
                  <div className="psem-day-header">
                    <span className="psem-day-name">{DAY_LABELS[i]}</span>
                    <span className={`psem-day-num ${hoje ? 'today' : ''}`}>{day.getDate()}</span>
                  </div>

                  {tarefas.length > 0 ? (
                    <>
                      <div className="psem-day-meta-dots">
                        {dayAreas.slice(0, 4).map(areaId => {
                          const cfg = AREA_CONFIG.find(a => a.id === areaId);
                          return cfg ? (
                            <div
                              key={areaId}
                              className="psem-area-dot"
                              style={{ background: cfg.color, boxShadow: `0 0 6px ${cfg.glow}` }}
                              title={cfg.label}
                            />
                          ) : null;
                        })}
                        {dayAreas.length > 4 && (
                          <span className="psem-area-dot-more">+{dayAreas.length - 4}</span>
                        )}
                      </div>

                      <div className="psem-day-progress">
                        <div className="psem-day-bar">
                          <div className="psem-day-fill" style={{ width: `${pct}%` }} />
                        </div>
                        <span>{concluidas}/{tarefas.length}</span>
                      </div>
                    </>
                  ) : (
                    <div className="psem-day-empty">
                      {passado ? 'Sem registro' : 'Não planejado'}
                    </div>
                  )}

                  {hoje && (
                    <div className="psem-day-plan-btn">
                      Planejar hoje
                      <FontAwesomeIcon icon={faChevronRight} style={{ marginLeft: 4 }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        <div className="psem-bottom-grid">

          {/* Metas desta semana */}
          <section className="psem-section psem-panel">
            <div className="psem-section-header">
              <h2>
                <FontAwesomeIcon icon={faTrophy} style={{ color: '#f59e0b', marginRight: 8 }} />
                Metas ativas
              </h2>
              <button className="psem-link-btn" onClick={() => navigate('/metas')}>
                Gerenciar <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>

            {metas.length === 0 ? (
              <div className="psem-panel-empty">
                <p>Nenhuma meta ativa.</p>
                <button className="psem-cta-btn" onClick={() => navigate('/metas')}>
                  Criar primeira meta →
                </button>
              </div>
            ) : (
              <div className="psem-metas-list">
                {metas.slice(0, 6).map(meta => {
                  const cfg = AREA_CONFIG.find(a => a.id === meta.area) || AREA_CONFIG[0];
                  const covered = metasCobertasIds.has(meta.id);
                  return (
                    <div key={meta.id} className={`psem-meta-row ${covered ? 'covered' : ''}`}>
                      <div
                        className="psem-meta-area-dot"
                        style={{ background: cfg.color, boxShadow: `0 0 8px ${cfg.glow}` }}
                      />
                      <div className="psem-meta-info">
                        <span className="psem-meta-titulo">{meta.titulo}</span>
                        <div className="psem-meta-progress-bar">
                          <div style={{ width: `${meta.progresso || 0}%`, background: cfg.color }} />
                        </div>
                      </div>
                      <div className="psem-meta-right">
                        <span className="psem-meta-pct">{meta.progresso || 0}%</span>
                        {covered ? (
                          <span className="psem-covered-badge">
                            <FontAwesomeIcon icon={faCheck} /> esta semana
                          </span>
                        ) : (
                          <span className="psem-uncovered-badge">sem tarefas</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Cobertura por área */}
          <section className="psem-section psem-panel">
            <div className="psem-section-header">
              <h2>
                <FontAwesomeIcon icon={faBolt} style={{ color: '#f59e0b', marginRight: 8 }} />
                Cobertura por área
              </h2>
            </div>

            {areaCoverage.length === 0 ? (
              <div className="psem-panel-empty">
                <p>Nenhuma área com tarefas planejadas.</p>
                <p style={{ fontSize: '0.8rem', opacity: 0.5 }}>
                  Vincule tarefas a metas para ver a cobertura semanal.
                </p>
              </div>
            ) : (
              <div className="psem-areas-list">
                {AREA_CONFIG.map(cfg => {
                  const cov = areaCoverage.find(a => a.id === cfg.id);
                  const diasCount = cov?.diasCount || 0;
                  const xp = cov?.xp || 0;
                  const barW = Math.min(100, (diasCount / 7) * 100);
                  return (
                    <div key={cfg.id} className="psem-area-row">
                      <div
                        className="psem-area-icon"
                        style={{ background: `${cfg.color}18`, color: cfg.color, border: `1px solid ${cfg.color}33` }}
                      >
                        <FontAwesomeIcon icon={cfg.icon} />
                      </div>
                      <div className="psem-area-info">
                        <div className="psem-area-top">
                          <span className="psem-area-label">{cfg.label}</span>
                          {diasCount > 0 && (
                            <span className="psem-area-xp" style={{ color: cfg.color }}>
                              +{xp} XP
                            </span>
                          )}
                        </div>
                        <div className="psem-area-bar">
                          <div
                            className="psem-area-fill"
                            style={{
                              width: `${barW}%`,
                              background: cfg.color,
                              boxShadow: diasCount > 0 ? `0 0 8px ${cfg.glow}` : 'none',
                            }}
                          />
                        </div>
                        <span className="psem-area-days">
                          {diasCount > 0 ? `${diasCount} dia${diasCount > 1 ? 's' : ''} coberto${diasCount > 1 ? 's' : ''}` : 'Sem cobertura esta semana'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        {/* Quick actions */}
        <div className="psem-quick-actions">
          <button
            className="psem-action-card"
            onClick={() => navigate('/levelup/dia')}
          >
            <div className="psem-action-icon" style={{ background: 'rgba(99,102,241,0.15)', color: '#818cf8' }}>
              <FontAwesomeIcon icon={faCalendarWeek} />
            </div>
            <div>
              <strong>Planejar hoje</strong>
              <small>Defina seu foco e tarefas para hoje</small>
            </div>
            <FontAwesomeIcon icon={faChevronRight} className="psem-action-arrow" />
          </button>

          <button
            className="psem-action-card"
            onClick={() => navigate('/metas')}
          >
            <div className="psem-action-icon" style={{ background: 'rgba(245,158,11,0.15)', color: '#fbbf24' }}>
              <FontAwesomeIcon icon={faTrophy} />
            </div>
            <div>
              <strong>Metas & Objetivos</strong>
              <small>Ajuste o progresso das suas metas</small>
            </div>
            <FontAwesomeIcon icon={faChevronRight} className="psem-action-arrow" />
          </button>

          <button
            className="psem-action-card"
            onClick={() => navigate('/levelup')}
          >
            <div className="psem-action-icon" style={{ background: 'rgba(16,185,129,0.15)', color: '#10b981' }}>
              <FontAwesomeIcon icon={faBolt} />
            </div>
            <div>
              <strong>LevelUp Hub</strong>
              <small>Veja seu XP, nível e conquistas</small>
            </div>
            <FontAwesomeIcon icon={faChevronRight} className="psem-action-arrow" />
          </button>
        </div>

      </div>
    </div>
  );
};

export default PlanejamentoSemanalPDIPage;
